import React, { useState, useEffect } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { usePayment } from '../hooks/usePayment';
import { dollarsToCents } from '../lib/stripe';

export function DigitalWalletButton({ orderData, onPaymentSuccess, onPaymentError }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  
  const stripe = useStripe();
  const { createPaymentIntent, processPayment } = usePayment();

  useEffect(() => {
    if (!stripe) return;

    // Create payment request for Apple Pay / Google Pay
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Total',
        amount: dollarsToCents(orderData.total),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: false,
      requestShipping: orderData.fulfillment === 'deliver',
      shippingOptions: orderData.fulfillment === 'deliver' ? [
        {
          id: 'standard',
          label: 'Standard Delivery',
          detail: '2-3 business days',
          amount: dollarsToCents(orderData.serviceFee || 0),
        },
      ] : [],
    });

    // Check if payment request is supported
    pr.canMakePayment().then((result) => {
      const applePay = result && result.applePay;
      const googlePay = result && result.googlePay;
      
      // Enhanced detection for various browsers and devices
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      // Check for Chrome (including Chromium-based browsers)
      const isChrome = userAgent.includes('Chrome') && !userAgent.includes('Edg');
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      
      // Check for Mac devices
      const isMac = platform.includes('Mac') || platform.includes('MacIntel') || platform.includes('MacPPC');
      
      // Check for mobile devices
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Chrome on Mac should support Google Pay
      const isChromeOnMac = isChrome && isMac;
      
      // Safari on Mac should support Apple Pay
      const isSafariOnMac = isSafari && isMac;
      
      // Mobile Chrome should support Google Pay
      const isMobileChrome = isChrome && isMobile;
      
      // If Stripe doesn't detect wallets but we're on a supported platform, enable them
      if (!googlePay && !applePay) {
        if (isChromeOnMac || isMobileChrome) {
          setGooglePayAvailable(true);
          setApplePayAvailable(false);
          setCanMakePayment(true);
        } else if (isSafariOnMac) {
          setApplePayAvailable(true);
          setGooglePayAvailable(false);
          setCanMakePayment(true);
        } else {
          setApplePayAvailable(applePay);
          setGooglePayAvailable(googlePay);
          setCanMakePayment(applePay || googlePay);
        }
      } else {
        setApplePayAvailable(applePay);
        setGooglePayAvailable(googlePay);
        setCanMakePayment(applePay || googlePay);
      }
    }).catch((error) => {
      // Fallback: Enhanced detection for various browsers and devices
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      const isChrome = userAgent.includes('Chrome') && !userAgent.includes('Edg');
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      const isMac = platform.includes('Mac') || platform.includes('MacIntel') || platform.includes('MacPPC');
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      const isChromeOnMac = isChrome && isMac;
      const isSafariOnMac = isSafari && isMac;
      const isMobileChrome = isChrome && isMobile;
      
      if (isChromeOnMac || isMobileChrome) {
        setGooglePayAvailable(true);
        setApplePayAvailable(false);
        setCanMakePayment(true);
      } else if (isSafariOnMac) {
        setApplePayAvailable(true);
        setGooglePayAvailable(false);
        setCanMakePayment(true);
      }
    });

    // Handle payment request events
    pr.on('paymentmethod', async (event) => {
      setLoading(true);
      setError('');

      try {
        // Create payment intent
        const paymentIntent = await createPaymentIntent({
          ...orderData,
          amount: dollarsToCents(orderData.total),
          currency: 'usd',
        });

        // Confirm the payment
        const { error: confirmError } = await stripe.confirmCardPayment(
          paymentIntent.client_secret,
          {
            payment_method: event.paymentMethod.id,
          },
          {
            handleActions: false,
          }
        );

        if (confirmError) {
          setError(confirmError.message);
          onPaymentError(confirmError.message);
        } else {
          // Payment successful
          const result = await processPayment(event.paymentMethod.id, {
            name: event.payerName,
            email: event.payerEmail,
          });
          
          if (result.success) {
            onPaymentSuccess(result.orderId);
          } else {
            onPaymentError(result.error);
          }
        }
      } catch (err) {
        setError(err.message);
        onPaymentError(err.message);
      } finally {
        setLoading(false);
      }
    });

    pr.on('cancel', () => {
      setError('Payment was cancelled');
    });

    setPaymentRequest(pr);
  }, [stripe, orderData, createPaymentIntent, processPayment, onPaymentSuccess, onPaymentError]);

  const handlePaymentRequest = () => {
    if (paymentRequest) {
      paymentRequest.show();
    }
  };

  // If wallets are available, return null since they're now handled in PaymentForm
  if (canMakePayment) {
    return null;
  }

  // Only show the "not available" message when wallets are not available
  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Digital Wallets</span>
        </div>
      </div>
      
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center">
        <p className="text-sm text-gray-600">
          Apple Pay and Google Pay are not available on this device.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Make sure you're using a supported device and browser.
        </p>
      </div>
    </div>
  );
}
